/**
 * Transcript loading functionality
 */

/**
 * Load transcript for a lecture
 * @param {string} lectureName - Lecture name (full filename without extension, e.g., "5205C20A – 7ACC-38 – Control")
 * @param {string} language - Language code (en, ru, de, etc.)
 * @returns {Promise<Array|null>} - Transcript data or null
 */
export async function loadTranscript(lectureName, language = 'en') {
	if (!lectureName) return null;
	
	try {
		// Extract year from first 2 digits of lecture name (e.g., 5205C20A -> 1952)
		const yearMatch = lectureName.match(/^(\d{2})/);
		if (!yearMatch) {
			console.warn(`[Transcript] Cannot extract year from lecture name: ${lectureName}`);
			return null;
		}
		const year = '19' + yearMatch[1];
		
		// Build language suffix
		// English: no suffix (just lectureName.json)
		// Other languages: lectureName_LANG.json (e.g., lectureName_RU.json)
		let langSuffix = '';
		if (language !== 'en' && language !== 'E') {
			langSuffix = '_' + language.toUpperCase();
		}
		
		// Encode lecture name for URL (handle special characters like spaces, dashes, etc.)
		const encodedName = encodeURIComponent(lectureName);
		const url = `/data/audio-lectures/transcripts/${year}/${encodedName}${langSuffix}.json`;
		
		const response = await fetch(url);
		
		if (!response.ok) {
			console.warn(`[Transcript] Failed to load transcript for ${lectureName} (${language}): ${response.status}`);
			return null;
		}
		
		const data = await response.json();
		
		// Check if we got an empty array (file doesn't exist but endpoint returned empty JSON)
		if (Array.isArray(data) && data.length === 0) {
			console.warn(`[Transcript] Empty transcript data for ${lectureName} (${language}) - file may not exist`);
			return null;
		}
		
		return Array.isArray(data) ? data : null;
	} catch (error) {
		console.error(`[Transcript] Error loading transcript for ${lectureName} (${language}):`, error);
		return null;
	}
}

/**
 * Check available languages for a lecture
 * @param {string} lectureName - Lecture name (full filename without extension)
 * @returns {Promise<Array<string>>} - Available language codes
 */
export async function getAvailableLanguages(lectureName) {
	if (!lectureName) return [];
	
	const yearMatch = lectureName.match(/^(\d{2})/);
	if (!yearMatch) return [];
	
	const year = '19' + yearMatch[1];
	const languages = ['en', 'ru', 'de']; // Common languages
	const available = [];
	const encodedName = encodeURIComponent(lectureName);
	
	for (const lang of languages) {
		const langSuffix = lang === 'en' ? '' : '_' + lang.toUpperCase();
		const url = `/data/audio-lectures/transcripts/${year}/${encodedName}${langSuffix}.json`;
		
		try {
			const response = await fetch(url, { method: 'HEAD' });
			if (response.ok) {
				// Also check if response is not empty JSON
				const dataResponse = await fetch(url);
				if (dataResponse.ok) {
					const data = await dataResponse.json();
					if (Array.isArray(data) && data.length > 0) {
						available.push(lang);
					}
				}
			}
		} catch (e) {
			// Ignore errors
		}
	}
	
	return available;
}

